import React from 'react';
import { Search, Filter, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { SEARCH } from '@/constants/app.constants';

interface SequenceSearchHeaderProps {
  query: string;
  sort: string;
  totalResults: number;
  searchTime: number;
  isLoading: boolean;
  shouldShowLanding: boolean;
  onQueryChange: (query: string) => void;
  onSortChange: (sort: string) => void;
  onToggleFilters: () => void;
  onSearch: (e: React.FormEvent) => void;
  showFilters: boolean;
}

const SORT_OPTIONS = [
  { value: SEARCH.SORT_OPTIONS.RELEVANCE, label: 'Relevance' },
  { value: SEARCH.SORT_OPTIONS.MOST_UPVOTED, label: 'Most Upvoted' },
  { value: SEARCH.SORT_OPTIONS.TRENDING, label: 'Trending' },
  { value: SEARCH.SORT_OPTIONS.MOST_DOWNLOADED, label: 'Most Downloaded' },
  { value: SEARCH.SORT_OPTIONS.HIGHEST_RATED, label: 'Highest Rated' },
  { value: SEARCH.SORT_OPTIONS.NEWEST_FIRST, label: 'Newest First' },
  { value: SEARCH.SORT_OPTIONS.PRICE_LOW_TO_HIGH, label: 'Price: Low to High' },
  { value: SEARCH.SORT_OPTIONS.PRICE_HIGH_TO_LOW, label: 'Price: High to Low' },
  { value: SEARCH.SORT_OPTIONS.TITLE_A_Z, label: 'Title A-Z' },
];

export const SequenceSearchHeader: React.FC<SequenceSearchHeaderProps> = ({
  query,
  sort,
  totalResults,
  searchTime,
  isLoading,
  shouldShowLanding,
  onQueryChange,
  onSortChange,
  onToggleFilters,
  onSearch,
  showFilters
}) => {
  return (
    <div className="bg-muted/30 border-b">
      <div className="container py-6 pt-24">
        <div className="max-w-4xl mx-auto">
          {!shouldShowLanding && (
            <h1 className="text-3xl font-bold mb-4">Light Display Sequences</h1>
          )}
          
          {/* Search Form */}
          <form onSubmit={onSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for songs, artists, genres..."
                className="pl-10 h-12"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
              />
            </div>
            <Button type="button" variant="outline" size="lg" onClick={onToggleFilters} className="lg:hidden">
              <Filter className="h-4 w-4" />
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <Select value={sort} onValueChange={onSortChange}>
                <SelectTrigger className="w-48 h-12">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>

          {/* Results Info */}
          <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <div>
              {isLoading ? (
                <span>Searching...</span>
              ) : (
                <span>
                  {totalResults > 0 
                    ? `${totalResults.toLocaleString()} sequences found`
                    : 'No sequences found'
                  }
                  {searchTime > 0 && (
                    <span className="ml-1">
                      ({searchTime}ms)
                    </span>
                  )}
                </span>
              )}
            </div>
            
            {/* Mobile Sort */}
            <div className="sm:hidden">
              <Select value={sort} onValueChange={onSortChange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};